import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { ArrowRight, Info, Star, CreditCard as CreditCardIcon } from 'lucide-react';

interface FeaturedCard {
  id: string;
  bank_id: string;
  bank_name: string;
  bank_logo: string;
  card_name: string;
  description: string;
  annual_fee: string;
  cashback?: string;
  welcome_bonus?: string;
  additional_card_fee?: string;
  features: string[];
  benefits: string[];
  campaign_title?: string;
  campaign_description?: string;
  redirect_url: string;
  is_featured: boolean;
  is_active: boolean;
}

const FeaturedCards = () => {
  const [featuredCards, setFeaturedCards] = useState<FeaturedCard[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    fetchFeaturedCards();
  }, []);

  const fetchFeaturedCards = async () => {
    try {
      setIsLoading(true);

      const { data, error } = await supabase
        .from('featured_cards')
        .select('*')
        .eq('is_featured', true)
        .eq('is_active', true)
        .order('display_order', { ascending: true });

      if (error) {
        console.error('Error fetching featured cards:', error);
        return;
      }

      setFeaturedCards(data || []);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#F7BA45] mx-auto mb-2"></div>
          <p className="text-sm text-slate-600">Yükleniyor...</p>
        </div>
      </div>
    );
  }

  if (featuredCards.length === 0) {
    return null;
  }

  return (
    <div className="mb-12">
      <div className="flex items-center gap-2 text-[#F7BA45] mb-6">
        <Star className="h-6 w-6" />
        <h2 className="text-2xl font-bold text-slate-800">
          Öne Çıkan Kredi Kartları
        </h2>
      </div>

      <div className="space-y-4">
        {featuredCards.map((card) => (
          <div
            key={card.id}
            className="bg-white rounded-xl border-2 border-blue-400 overflow-hidden hover:shadow-2xl transition-all duration-300"
          >
            <div className="p-6">
              {/* Header */}
              <div className="flex items-start justify-between mb-6">
                <div className="flex items-center gap-4">
                  <img
                    src={card.bank_logo}
                    alt={card.bank_name}
                    className="h-14 w-14 object-contain"
                  />
                  <div>
                    <h3 className="text-xl font-bold text-slate-800">{card.card_name}</h3>
                    <p className="text-sm text-slate-600">{card.bank_name}</p>
                  </div>
                </div>
              </div>

              {/* Stats Grid */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                <div className="bg-slate-50 rounded-lg p-4">
                  <div className="text-sm text-slate-600 mb-1">Yıllık Ücret</div>
                  <div className="text-xl font-bold text-slate-800">{card.annual_fee}</div>
                </div>
                <div className="bg-slate-50 rounded-lg p-4">
                  <div className="text-sm text-slate-600 mb-1">Puan/Mil Kazanımı</div>
                  <div className="text-xl font-bold text-slate-800">{card.cashback || '%1.5'}</div>
                </div>
                <div className="bg-slate-50 rounded-lg p-4">
                  <div className="text-sm text-slate-600 mb-1">Hoş Geldin Bonusu</div>
                  <div className="text-xl font-bold text-slate-800">{card.welcome_bonus || '5000 Mil'}</div>
                </div>
                <div className="bg-slate-50 rounded-lg p-4">
                  <div className="text-sm text-slate-600 mb-1">Ek Kart Ücreti</div>
                  <div className="text-xl font-bold text-slate-800">{card.additional_card_fee || '565 ₺'}</div>
                </div>
              </div>

              {/* Campaign */}
              {card.campaign_title && (
                <div className="mb-6 p-4 bg-amber-50 rounded-lg flex items-start gap-3">
                  <div className="w-8 h-8 rounded-full bg-amber-200 flex items-center justify-center flex-shrink-0">
                    <svg className="w-5 h-5 text-amber-700" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M11 3a1 1 0 10-2 0v1a1 1 0 102 0V3zM15.657 5.757a1 1 0 00-1.414-1.414l-.707.707a1 1 0 001.414 1.414l.707-.707zM18 10a1 1 0 01-1 1h-1a1 1 0 110-2h1a1 1 0 011 1zM5.05 6.464A1 1 0 106.464 5.05l-.707-.707a1 1 0 00-1.414 1.414l.707.707zM5 10a1 1 0 01-1 1H3a1 1 0 110-2h1a1 1 0 011 1zM8 16v-1h4v1a2 2 0 11-4 0zM12 14c.015-.34.208-.646.477-.859a4 4 0 10-4.954 0c.27.213.462.519.476.859h4.002z" />
                    </svg>
                  </div>
                  <div className="flex-1">
                    <div className="font-semibold text-slate-800 mb-1">{card.campaign_title}</div>
                    {card.campaign_description && (
                      <div className="text-sm text-slate-600">{card.campaign_description}</div>
                    )}
                    <div className="flex items-center gap-1 text-xs text-slate-500 mt-2">
                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clipRule="evenodd" />
                      </svg>
                      Son Geçerlilik: 31.12.2024
                    </div>
                  </div>
                </div>
              )}

              {/* Footer */}
              <div className="flex items-center justify-between pt-4 border-t border-slate-200">
                <div className="flex items-center gap-2 text-slate-600">
                  <Info size={18} />
                  <span className="text-sm">Başvuru için {card.bank_name}'a yönlendirilirsiniz</span>
                </div>
                <div className="flex items-center gap-3">
                  <a
                    href={card.redirect_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[#F7BA45] hover:text-[#e5ac3d] text-sm font-medium transition-colors flex items-center gap-1"
                  >
                    Detaylı Bilgi
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                    </svg>
                  </a>
                  <a
                    id={`card-apply-${card.bank_id}`}
                    data-bank-name={card.bank_name}
                    data-card-name={card.card_name}
                    href={card.redirect_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="bg-[#F7BA45] hover:bg-[#e5ac3d] text-slate-900 px-6 py-2.5 rounded-lg transition-all duration-300 flex items-center gap-2 shadow-md hover:shadow-lg font-semibold"
                  >
                    HEMEN BAŞVUR
                    <ArrowRight size={18} />
                  </a>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default FeaturedCards;
